import { useState, useCallback } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

export function LoginView() {
  const needsSetup = useAuthStore((s) => s.needsSetup);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  // #1113: set only when a definitive refresh rejection ended the session.
  const sessionEnded = useAuthStore((s) => s.signedOutReason === 'session-ended');
  const login = useAuthStore((s) => s.login);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const isLoading = status === 'loading';
  const isBootstrap = needsSetup;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!username.trim() || !password.trim()) return;
      if (isBootstrap) {
        await bootstrap(username.trim(), password.trim());
      } else {
        await login(username.trim(), password.trim());
      }
    },
    [username, password, isBootstrap, bootstrap, login],
  );

  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <div className="w-full max-w-sm space-y-6 px-4">
        {/* Header */}
        <div className="text-center">
          <div className="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
            <ShieldCheck className="text-primary h-6 w-6" />
          </div>
          <h1 className="text-foreground text-xl font-semibold">
            {isBootstrap ? 'Create Admin Account' : 'Sign In'}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isBootstrap
              ? 'Set up the first platform administrator.'
              : 'Enter your credentials to continue.'}
          </p>
        </div>

        {/* #1113: why the trader is here, in neutral styling (not the red error). Outside the form, so the
            form's own error line stays the only message inside it. */}
        {sessionEnded && !isBootstrap && (
          <p role="status" className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
            Your session ended. Sign in again; orders already sent are unaffected. Check Open Orders after signing in.
          </p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-card border-border space-y-4 rounded-lg border p-5">
            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                className="bg-background border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder="admin"
              />
            </div>

            <div>
              <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isBootstrap ? 'new-password' : 'current-password'}
                className="bg-background border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                placeholder={isBootstrap ? 'Choose a strong password' : '••••••••'}
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !username.trim() || !password.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600"
            >
              {isLoading && <Loader2 size={14} className="animate-spin" />}
              {isBootstrap
                ? isLoading
                  ? 'Creating...'
                  : 'Create Admin'
                : isLoading
                  ? 'Signing in...'
                  : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
